import { InfiniteData, useMutation, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import {
  ConversationTimelinePage,
  getConversationTimelineQueryKey,
} from "modules/conversations/lib/conversationTimelineQuery"
import { getConversationQueryKey } from "modules/conversations/lib/conversationQuery"
import { getMessagesQueryKey, MessagesPage } from "modules/messages/lib/messagesQuery"
import {
  ConversationUploadedImageSchema,
  DataResponse,
  MessageFormatEnum,
  MessageResponseDataSchema,
  MessageRoleEnum,
} from "schema"
import { z } from "zod"

type Params = {
  conversationId: string
  content?: string
  uploadedImages?: UploadedConversationImage[]
}

type Props = {
  onError?: (error: Error) => void
}

type MessageResponse = z.infer<typeof MessageResponseDataSchema>
type UploadedConversationImage = z.infer<typeof ConversationUploadedImageSchema> & {
  previewUrl?: string
}
type Response = DataResponse<MessageResponse | MessageResponse[]>

export function useCreateTestingMessageMutation(props: Props) {
  const queryClient = useQueryClient()

  return useMutation<Response, Error, Params>({
    mutationFn: async (params) => {
      const uploadedImages = params.uploadedImages?.map(({ mediaUrl, format }) => ({
        mediaUrl,
        format,
      }))

      const response = await axios.post<Response>(
        `/conversations/testing/${params.conversationId}/messages`,
        {
          content: params.content || undefined,
          uploadedImages: uploadedImages?.length ? uploadedImages : undefined,
        },
      )

      return response.data
    },
    onMutate: async (newMessage) => {
      const timelineKey = getConversationTimelineQueryKey(newMessage.conversationId)
      const messagesKey = getMessagesQueryKey(newMessage.conversationId)
      const previousTimeline =
        queryClient.getQueryData<InfiniteData<ConversationTimelinePage>>(timelineKey)
      const previousMessages = queryClient.getQueryData<InfiniteData<MessagesPage>>(messagesKey)

      const uploadedImages = newMessage.uploadedImages ?? []
      const now = new Date()
      const optimisticMessages: z.infer<typeof MessageResponseDataSchema>[] = uploadedImages.length
        ? uploadedImages.map((image, index) => ({
            id: `temp-${now.getTime()}-${index}`,
            createdAt: now.toISOString(),
            content: index === 0 ? (newMessage.content ?? null) : null,
            role: MessageRoleEnum.User,
            conversationId: newMessage.conversationId,
            format: image.format,
            mediaUrl: image.previewUrl ?? image.mediaUrl,
          }))
        : [
            {
              id: `temp-${now.getTime()}`,
              createdAt: now.toISOString(),
              content: newMessage.content ?? "",
              role: MessageRoleEnum.User,
              conversationId: newMessage.conversationId,
              format: MessageFormatEnum.Text,
              mediaUrl: null,
            },
          ]

      queryClient.setQueryData<InfiniteData<ConversationTimelinePage>>(timelineKey, (oldData) => {
        if (!oldData || oldData.pages.length === 0) return oldData
        const firstPage = oldData.pages[0]!
        const newestMessageId = optimisticMessages.at(-1)?.id ?? firstPage.pageInfo.newestMessageId
        const pages = [
          {
            ...firstPage,
            messages: [...firstPage.messages, ...optimisticMessages],
            pageInfo: {
              ...firstPage.pageInfo,
              newestMessageId,
            },
          },
          ...oldData.pages.slice(1),
        ]
        return { ...oldData, pages }
      })

      queryClient.setQueryData<InfiniteData<MessagesPage>>(messagesKey, (oldData) => {
        if (!oldData || oldData.pages.length === 0) return oldData
        const firstPage = oldData.pages[0]!
        const newestId = optimisticMessages.at(-1)?.id ?? firstPage.pageInfo.newestId
        const pages = [
          {
            ...firstPage,
            messages: [...firstPage.messages, ...optimisticMessages],
            pageInfo: {
              ...firstPage.pageInfo,
              newestId,
            },
          },
          ...oldData.pages.slice(1),
        ]
        return { ...oldData, pages }
      })

      return { previousTimeline, previousMessages }
    },
    onError: (err, newMessage, context) => {
      const typedContext = context as {
        previousTimeline: InfiniteData<ConversationTimelinePage> | undefined
        previousMessages: InfiniteData<MessagesPage> | undefined
      }

      props.onError?.(err)

      if (typedContext.previousTimeline) {
        queryClient.setQueryData(
          getConversationTimelineQueryKey(newMessage.conversationId),
          typedContext.previousTimeline,
        )
      }

      if (typedContext.previousMessages) {
        queryClient.setQueryData(
          getMessagesQueryKey(newMessage.conversationId),
          typedContext.previousMessages,
        )
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: getConversationTimelineQueryKey(variables.conversationId),
      })
      queryClient.invalidateQueries({
        queryKey: getMessagesQueryKey(variables.conversationId),
      })
      queryClient.invalidateQueries({
        queryKey: getConversationQueryKey(variables.conversationId),
      })
    },
  })
}
